import { UseInfiniteQueryResult } from "@tanstack/react-query";
import { concat, last } from "lodash";
import { getAPIFilters } from "PFApp/use_filtered_collection";
import { useMarketplaceInterestedActivitiesInfinite } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_interested_activities";
import { useMarketplaceSavedActivitiesInfinite } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_saved_activities";
import { useMarketplaceSharedActivitiesInfinite } from "PFCore/hooks/queries/activities/marketplace/use_marketplace_shared_activities";
import { useMatchingActivitiesInfinite } from "PFCore/hooks/queries/activities/matching_activities/use_matching_activities";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { Collection, MarketplaceActivity, MatchedActivity, Meta } from "PFTypes";
import { QueryOptions } from "PFTypes/request_options";

import { WorkOpportunitiesTab } from "../../constants";
import { useMarketplaceContext } from "../../contexts/marketplace_context";
import { useMatchingActivitiesFilters } from "../../hooks/use_matching_activities_filters";
import { useWorkOpportunitiesTab } from "./use_work_opportunities_tab";

const PER_PAGE = 5;

const EMPTY_RETURN_DATA: RolesBrowserQueryData = {
  roles: [],
  meta: { total: 0 },
  isLoading: true,
  fetchNextPage: () => new Promise(() => {}),
  hasNextPage: false
};

export type RolesBrowserQueryData = {
  roles: MatchedActivity[] | MarketplaceActivity[];
  meta: Meta;
  isLoading: boolean;
  fetchNextPage: UseInfiniteQueryResult["fetchNextPage"];
  hasNextPage: UseInfiniteQueryResult["hasNextPage"];
};

export type UseRolesBrowserReturn = {
  queryData: RolesBrowserQueryData;
};

type RolesInfiniteQueryResult =
  | UseInfiniteQueryResult<Collection<MatchedActivity[]>>
  | UseInfiniteQueryResult<Collection<MarketplaceActivity[]>>;

const mergeRolesPages = (pagesData: RolesInfiniteQueryResult["data"]): RolesBrowserQueryData["roles"] =>
  concat(...(pagesData?.pages.map(({ entries }) => entries) || []));

const getInfiniteQueryMeta = (pagesData: UseInfiniteQueryResult<Collection<any>>["data"]): Meta =>
  last(pagesData?.pages)?.meta || { total: 0 };

export const useRolesBrowser = (): UseRolesBrowserReturn => {
  const { data: currentProfile } = useCurrentProfile();
  const profileId = currentProfile?.id;

  const { relationType, getFiltersData, getOrderData } = useMarketplaceContext();
  const currentTab = useWorkOpportunitiesTab();
  const matchingActivitiesFilters = useMatchingActivitiesFilters();

  const commonOptions: QueryOptions<any> = {
    keepPreviousData: true
  };

  const {
    data: matchingActivitiesData,
    isFetching: isLoadingMatchingActivities,
    fetchNextPage: fetchNextMatchingActivitiesPage,
    hasNextPage: hasNextMatchingActivitiesPage
  } = useMatchingActivitiesInfinite(
    {
      perPage: PER_PAGE,
      profileId,
      filters: getAPIFilters(matchingActivitiesFilters),
      order: getOrderData(WorkOpportunitiesTab.MatchingRoles) || undefined,
      relationType
    },
    {
      ...commonOptions,
      enabled: currentTab === WorkOpportunitiesTab.MatchingRoles
    }
  );

  const {
    data: interestedActivitiesData,
    isFetching: isLoadingInterestedActivities,
    fetchNextPage: fetchNextInterestedActivitiesPage,
    hasNextPage: hasNextInterestedActivitiesPage
  } = useMarketplaceInterestedActivitiesInfinite(
    {
      perPage: PER_PAGE,
      filters: getAPIFilters(getFiltersData(WorkOpportunitiesTab.Interested)),
      order: getOrderData(WorkOpportunitiesTab.Interested) || undefined
    },
    {
      ...commonOptions,
      enabled: currentTab === WorkOpportunitiesTab.Interested
    }
  );

  const {
    data: savedActivitiesData,
    isFetching: isLoadingSavedActivities,
    fetchNextPage: fetchNextSavedActivitiesPage,
    hasNextPage: hasNextSavedActivitiesPage
  } = useMarketplaceSavedActivitiesInfinite(
    {
      perPage: PER_PAGE,
      filters: getAPIFilters(getFiltersData(WorkOpportunitiesTab.SavedRoles)),
      order: getOrderData(WorkOpportunitiesTab.SavedRoles) || undefined
    },
    {
      ...commonOptions,
      enabled: currentTab === WorkOpportunitiesTab.SavedRoles
    }
  );

  const {
    data: sharedActivitiesData,
    isFetching: isLoadingSharedActivities,
    fetchNextPage: fetchNextSharedActivitiesPage,
    hasNextPage: hasNextSharedActivitiesPage
  } = useMarketplaceSharedActivitiesInfinite(
    {
      perPage: PER_PAGE,
      filters: getAPIFilters(getFiltersData(WorkOpportunitiesTab.SharedWithMe)),
      order: getOrderData(WorkOpportunitiesTab.SharedWithMe) || undefined
    },
    {
      ...commonOptions,
      enabled: currentTab === WorkOpportunitiesTab.SharedWithMe
    }
  );

  const queryDataMap: Record<WorkOpportunitiesTab, RolesBrowserQueryData> = {
    [WorkOpportunitiesTab.MatchingRoles]: {
      roles: mergeRolesPages(matchingActivitiesData),
      meta: getInfiniteQueryMeta(matchingActivitiesData),
      isLoading: isLoadingMatchingActivities,
      fetchNextPage: fetchNextMatchingActivitiesPage,
      hasNextPage: hasNextMatchingActivitiesPage
    },
    [WorkOpportunitiesTab.Interested]: {
      roles: mergeRolesPages(interestedActivitiesData),
      meta: getInfiniteQueryMeta(interestedActivitiesData),
      isLoading: isLoadingInterestedActivities,
      fetchNextPage: fetchNextInterestedActivitiesPage,
      hasNextPage: hasNextInterestedActivitiesPage
    },
    [WorkOpportunitiesTab.SavedRoles]: {
      roles: mergeRolesPages(savedActivitiesData),
      meta: getInfiniteQueryMeta(savedActivitiesData),
      isLoading: isLoadingSavedActivities,
      fetchNextPage: fetchNextSavedActivitiesPage,
      hasNextPage: hasNextSavedActivitiesPage
    },
    [WorkOpportunitiesTab.SharedWithMe]: {
      roles: mergeRolesPages(sharedActivitiesData),
      meta: getInfiniteQueryMeta(sharedActivitiesData),
      isLoading: isLoadingSharedActivities,
      fetchNextPage: fetchNextSharedActivitiesPage,
      hasNextPage: hasNextSharedActivitiesPage
    }
  };

  const queryData: RolesBrowserQueryData = currentTab ? queryDataMap[currentTab] : EMPTY_RETURN_DATA;

  return { queryData };
};
